import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Text,
  Stack,
  Box,
} from "@chakra-ui/react";
import { Select } from "antd";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useStores } from "stores/setup/use-store";
import { useEffect, useCallback, useMemo } from "react";

interface AlertFiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onDashboardChange: (value: number) => void;
  onLocationChange: (value: number) => void;
  onAlertTypeChange: (value: string) => void;
}

export const AlertFiltersDrawer = observer(
  ({
    isOpen,
    onClose,
    onDashboardChange,
    onLocationChange,
    onAlertTypeChange,
  }: AlertFiltersDrawerProps) => {
    const {
      dataStores: { insightAlertStore },
    } = useStores();

    const { t } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.insightAlert",
    });

    useEffect(() => {
      if (
        insightAlertStore.operationRuleDetailSelected &&
        insightAlertStore.operationRuleDetailSelected !== 0
      ) {
        insightAlertStore.getOperationRuleLocationAPI(
          insightAlertStore.operationRuleDetailSelected
        );
      }
    }, [insightAlertStore.operationRuleDetailSelected]);

    const filterOption = (
      input: string,
      option?: { label?: string; value?: string }
    ) => {
      return (
        option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ??
        false
      );
    };

    const applyFilters = useCallback(() => {
      if (insightAlertStore.alertTypeDetailSelected) {
        insightAlertStore.setAlertTypeDetailSelected(
          insightAlertStore.alertTypeDetailSelected
        );
        insightAlertStore.setIsFilterApplied(true);
        onAlertTypeChange(insightAlertStore.alertTypeDetailSelected);
        onClose();
        return;
      }

      if (
        insightAlertStore.operationRuleDetailSelected &&
        !insightAlertStore.operationRuleLocationDetailSelected
      ) {
        insightAlertStore.setIsFilterApplied(true);
        onDashboardChange(insightAlertStore.operationRuleDetailSelected);
        onClose();
        return;
      }

      if (
        insightAlertStore.operationRuleDetailSelected &&
        insightAlertStore.operationRuleLocationDetailSelected
      ) {
        insightAlertStore.setOperationRuleDetailSelected(
          insightAlertStore.operationRuleDetailSelected
        );
        insightAlertStore.setIsFilterApplied(true);
        onLocationChange(insightAlertStore.operationRuleLocationDetailSelected);
        onClose();
        return;
      }

      if (
        !insightAlertStore.alertTypeDetailSelected &&
        !insightAlertStore.operationRuleDetailSelected &&
        !insightAlertStore.operationRuleLocationDetailSelected
      ) {
        insightAlertStore.getOperationRuleAlertForTableAPI({});
        onClose();
      }
    }, [
      insightAlertStore,
      onAlertTypeChange,
      onDashboardChange,
      onLocationChange,
      onClose,
    ]);

    const clearFilters = () => {
      insightAlertStore.clearFilters();
      onClose();
    };

    const dashboardsInAlerts = useMemo(() => {
      return insightAlertStore.operationRule.filter((dashboard) =>
        insightAlertStore.operationRuleAlertForTableDatail.some(
          (template) => template?.operationRuleId === dashboard.id
        )
      );
    }, [
      insightAlertStore.operationRule,
      insightAlertStore.operationRuleAlertForTableDatail,
    ]);

    const alertTypesInTable = useMemo(() => {
      return insightAlertStore.alertTypes.filter((alertType) =>
        insightAlertStore.operationRuleAlertForTableDatail.some(
          (alert) => alert.alertType === alertType
        )
      );
    }, [
      insightAlertStore.operationRuleAlertForTableDatail,
      insightAlertStore.alertTypes,
    ]);

    return (
      <Drawer
        size="md"
        isOpen={isOpen}
        placement="right"
        onClose={() => {
          onClose();
          if (!insightAlertStore.isFilterApplied) {
            insightAlertStore.setAlertTypeDetailSelected(null);
            insightAlertStore.setOperationRuleDetailSelected(0);
            insightAlertStore.setOperationRuleLocationDetailSelected(0);
          }
        }}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton color={"black"} />
          <DrawerHeader>
            <Text color="black" mt={"3px"}>
              {t("filter")}
            </Text>
          </DrawerHeader>

          <DrawerBody color="black">
            <Stack spacing={8}>
              <Box>
                <Text fontWeight={"600"}>{t("alertType")}</Text>
                <Select
                  className="custom-select-filter"
                  showSearch
                  optionFilterProp="children"
                  style={{
                    width: "100%",
                    height: "40px",
                    borderColor: "#eff0f3",
                  }}
                  value={insightAlertStore.alertTypeDetailSelected}
                  dropdownStyle={{ zIndex: 2000 }}
                  filterOption={filterOption}
                  onChange={(value) =>
                    insightAlertStore.setAlertTypeDetailSelected(value)
                  }
                  allowClear
                  disabled={
                    insightAlertStore.operationRuleDetailSelected > 0 ||
                    insightAlertStore.operationRuleLocationDetailSelected > 0
                  }
                >
                  <Select.Option value={0} key={0}>
                    {" "}
                  </Select.Option>
                  {alertTypesInTable.map((alertType) => (
                    <Select.Option
                      key={alertType}
                      value={alertType}
                      label={`${t(alertType)}`}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{t(alertType)}</span>
                        <span
                          style={{
                            marginRight: "16px",
                            color: "#086AA9",
                          }}
                        ></span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontWeight={"600"}>{t("dashboard")}</Text>
                <Select
                  className="custom-select-filter"
                  showSearch
                  optionFilterProp="children"
                  style={{
                    width: "100%",
                    height: "40px",
                    borderColor: "#eff0f3",
                  }}
                  value={insightAlertStore.operationRuleDetailSelected}
                  onChange={(value) =>
                    insightAlertStore.setOperationRuleDetailSelected(value)
                  }
                  dropdownStyle={{ zIndex: 2000 }}
                  allowClear
                  disabled={insightAlertStore.alertTypeDetailSelected !== null}
                >
                  <Select.Option value={0} key={0}>
                    {" "}
                  </Select.Option>
                  {dashboardsInAlerts.map((value) => (
                    <Select.Option value={value.id} key={value.id}>
                      {value.name}
                    </Select.Option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontWeight={"600"}>{t("location")}</Text>
                <Select
                  className="custom-select-filter"
                  showSearch
                  optionFilterProp="children"
                  style={{
                    width: "100%",
                    height: "40px",
                    borderColor: "#eff0f3",
                  }}
                  value={insightAlertStore.operationRuleLocationDetailSelected}
                  onChange={(value) =>
                    insightAlertStore.setOperationRuleLocationDetailSelected(
                      value
                    )
                  }
                  dropdownStyle={{ zIndex: 2000 }}
                  disabled={
                    insightAlertStore.operationRuleDetailSelected === 0 ||
                    insightAlertStore.alertTypeDetailSelected !== null
                  }
                  allowClear
                >
                  <Select.Option value={0} key={0}>
                    {" "}
                  </Select.Option>
                  {insightAlertStore.operationRuleLocation.map((value) => (
                    <Select.Option value={value.id} key={value.id}>
                      {value.name}
                    </Select.Option>
                  ))}
                </Select>
              </Box>
            </Stack>
          </DrawerBody>

          <DrawerFooter>
            <Button
              variant="ghost"
              color="newBlue.500"
              mr={3}
              onClick={clearFilters}
            >
              {t("clear")}
            </Button>
            <Button
              layerStyle="ocean"
              _hover={{ layerStyle: "ocean" }}
              onClick={applyFilters}
            >
              {t("filtrate")}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
);
